import { createAsyncThunk } from '@reduxjs/toolkit';
import type { Role, Permission, PermissionGroup } from '@/types';
import api from '@/api/client';
import { setRoles, editRole, removeRole } from '../slices/roleSlice';
import { setToast } from '../slices/toastSlice';

export const fetchPermissionGroups = createAsyncThunk<PermissionGroup[], void, { rejectValue: string }>(
  'role/fetchPermissionGroups',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get<{ data: PermissionGroup[] }>('/roles/permissions');
      return Array.isArray(data?.data) ? data.data : [];
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur chargement permissions';
      return rejectWithValue(message);
    }
  }
);

export const grantPermissionToRole = createAsyncThunk<
  void,
  { roleId: number; permissionParentId: number; permissions: Permission[] },
  { rejectValue: string }
>(
  'role/grantPermission',
  async ({ roleId, permissionParentId, permissions }, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/roles/grant-permission/${roleId}/${permissionParentId}`, { permissions });
      await dispatch(fetchRoles());
      dispatch(setToast({ message: 'Permissions mises à jour', severity: 'success' }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur mise à jour permissions';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const fetchRoles = createAsyncThunk('role/fetch', async (_, { dispatch, rejectWithValue }) => {
  try {
    const { data } = await api.get<{ data: Role[] }>('/roles');
    dispatch(setRoles(data.data ?? []));
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Erreur chargement rôles';
    dispatch(setToast({ message, severity: 'error' }));
    return rejectWithValue(message);
  }
});

export const createRole = createAsyncThunk<boolean, Partial<Role>, { rejectValue: string }>(
  'role/create',
  async (role, { dispatch, rejectWithValue }) => {
    try {
      await api.post('/roles', role);
      await dispatch(fetchRoles());
      dispatch(setToast({ message: 'Rôle créé', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur création';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const updateRole = createAsyncThunk<boolean, Role, { rejectValue: string }>(
  'role/update',
  async (role, { dispatch, rejectWithValue }) => {
    try {
      await api.put(`/roles/${role.id}`, role);
      dispatch(editRole(role));
      dispatch(setToast({ message: 'Rôle modifié', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur modification';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);

export const deleteRole = createAsyncThunk<boolean, Role, { rejectValue: string }>(
  'role/delete',
  async (role, { dispatch, rejectWithValue }) => {
    try {
      await api.delete(`/roles/${role.id}`);
      dispatch(removeRole(role));
      dispatch(setToast({ message: 'Rôle supprimé', severity: 'success' }));
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur suppression';
      dispatch(setToast({ message, severity: 'error' }));
      return rejectWithValue(message);
    }
  }
);
